/**
 * useArTableCacheHydration
 *
 * Background hook that hydrates cached column descriptions for entity reference columns.
 * Updates local Valtio state only - does not persist to Firestore.
 * Cache updates to tableTypes should be done by admins or cloud functions.
 */

import { useEffect, useRef, useContext } from 'react';
import { subscribe } from 'valtio';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '@/services/startFirestore';
import { useStoreNonReactive } from '@/store';
import { UserContext } from '@/providers/UserContext';
import type {
  ArTableType,
  ArColumnDefinition,
  ArColumnReference,
} from '#types/System.Artifacts.types';
import type { Entity } from '#types/System.types';
import type { DocumentState } from '@/store/Store.types';

/**
 * Checks if a column is an entity reference column
 */
const isEntityReferenceColumn = (
  column: ArColumnDefinition
): column is ArColumnReference & { referenceType: 'entity'; entityId: string } => {
  return (
    column.uiElementType === 'reference' &&
    'referenceType' in column &&
    column.referenceType === 'entity' &&
    'entityId' in column &&
    typeof column.entityId === 'string'
  );
};

/**
 * Fetches an entity definition from Firestore
 */
const fetchEntityDefinition = async (entityId: string): Promise<Entity | null> => {
  const entityRef = doc(firestore, 'entities', entityId);
  const snapshot = await getDoc(entityRef);
  if (snapshot.exists()) {
    return snapshot.data() as Entity;
  }
  return null;
};

/**
 * Hook that hydrates entity reference column cacheDescription in local state.
 * Does not persist to Firestore - cache updates should be done by admins.
 */
export function useArTableCacheHydration(): void {
  const { state } = useStoreNonReactive('schema');
  const { isLoggedIn } = useContext(UserContext);
  const hasHydratedRef = useRef(false);
  const hasScheduledRef = useRef(false);

  useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!isLoggedIn) return;

    const schemaState = state as DocumentState<ArTableType>;

    const maybeSchedule = (): void => {
      if (hasHydratedRef.current) return;
      if (hasScheduledRef.current) return;
      if (schemaState.loading) return;

      const columns = schemaState.data?.columns;
      if (!columns) return;

      hasScheduledRef.current = true;

      const hydrateCache = async () => {
        const entityColumns = columns.filter(isEntityReferenceColumn);

        if (entityColumns.length === 0) {
          hasHydratedRef.current = true;
          return;
        }

        let hasUpdates = false;

        await Promise.all(
          entityColumns.map(async (column) => {
            const entity = await fetchEntityDefinition(column.entityId);
            if (!entity) return;

            const entityLabel = entity.description?.shortLabel;
            if (!entityLabel) return;

            const currentLabel = column.cacheDescription;
            if (currentLabel === entityLabel) return;

            const columnIndex = columns.findIndex((c) => c.typeId === column.typeId);
            if (columnIndex !== -1 && schemaState.data?.columns) {
              schemaState.data.columns[columnIndex] = {
                ...schemaState.data.columns[columnIndex],
                cacheDescription: entityLabel,
              };
              hasUpdates = true;
            }
          })
        );

        if (hasUpdates) {
          // Local state updated via Valtio mutation - no Firestore write
        }

        hasHydratedRef.current = true;
      };

      const runInBackground = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 100));
      runInBackground(() => {
        hydrateCache().catch((error) => {
          console.error('ArTable cache hydration failed:', error);
        });
      });
    };

    const unsubscribe = subscribe(schemaState, maybeSchedule);
    maybeSchedule();
    return unsubscribe;
  }, [state, isLoggedIn]);
}

export default useArTableCacheHydration;

